import { createContext, useState, useContext } from 'react'
import type { FC, HTMLProps, HTMLAttributes, ReactNode } from 'react'
import cn from 'classnames'
import { TabProps, TabItemProps } from '.'
import s from './tab.module.css'
import { TabContentProps } from './types'

export type TabContextProps = {
  active: number
  setActive: Function
}

const TabContext = createContext({} as TabContextProps)

function useTab() {
  const context = useContext(TabContext)
  const { active, setActive } = context
  return { active, setActive }
}

function TabProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<number>(0 as number)
  return <TabContext.Provider value={{ active, setActive }}>{children}</TabContext.Provider>
}

function Tab({ children, ...props }: TabProps) {
  return (
    <TabProvider>
      <div {...props} className={cn(s.root)}>
        {children}
      </div>
    </TabProvider>
  )
}

function Tabs({
  children,
  current,
  vertical,
  ...props
}: // eslint-disable-next-line react/require-default-props
HTMLAttributes<HTMLDivElement> & { current: number; vertical?: boolean }) {
  const { active, setActive } = useTab()

  if (active === 0) {
    setActive(current)
  }

  return (
    <div {...(props as HTMLProps<HTMLDivElement>)} className={cn(s.tabs, vertical && s.vertical)}>
      {children}
    </div>
  )
}

const Item: FC<TabItemProps> = ({ children, index, disabled, ...props }) => {
  const { active, setActive } = useTab()

  return (
    <button
      {...(props as HTMLProps<HTMLButtonElement>)}
      type="button"
      className={cn(s.tab, active === index && s.tabActive)}
      onClick={disabled ? () => {} : () => setActive(index)}
    >
      {children}
    </button>
  )
}

const Content: FC<TabContentProps> = ({ children, className, index, ...props }) => {
  const { active } = useTab()
  return (
    <div className={cn(s.content, className, active === index && s.contentActive)} {...props}>
      {children}
    </div>
  )
}

Tab.Item = Item
Tab.Tabs = Tabs
Tab.Content = Content

export default Tab
